import { collection, getDocs, writeBatch, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/index';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllUsers, updateUserProfile, deleteUserDoc } from '../firebase/db';
import { createUserAsAdmin } from '../firebase/auth';
import { USER_ROLES } from '../firebase/schema';
import { getCompanySettings, updateCompanySettings, initializeDefaultSettings } from '../firebase/settings';
import AuthEventLog from '../components/AuthEventLog';
import './Admin.css';

const Admin = () => {
  const { isAdmin, isManager, currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'fieldWorker' });
  const isFieldWorkerRole = newUser.role === 'fieldWorker';
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  
  // PIN reset state
  const [resetPinUid, setResetPinUid] = useState(null);
  const [resetPinEmail, setResetPinEmail] = useState('');
  const [newPin, setNewPin] = useState('');
  const [resetPinError, setResetPinError] = useState('');
  const [resetPinSuccess, setResetPinSuccess] = useState('');

  // Company Settings state
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const loadUsers = async () => {
    setUsersLoading(true);
    const data = await getAllUsers();
    setUsers(data);
    setUsersLoading(false);
  };

  const loadSettings = async () => {
    if (!isAdmin) return;
    setSettingsLoading(true);
    try {
      const existing = await getCompanySettings();
      if (!existing?.exists) {
        // Initialize defaults
        await initializeDefaultSettings(currentUser?.uid);
        const fresh = await getCompanySettings();
        setSettings(fresh);
      } else {
        setSettings(existing);
      }
    } catch (e) {
      console.error('Failed to load settings:', e);
    }
    setSettingsLoading(false);
  };

  useEffect(() => {
    if (isAdmin || isManager) loadUsers();
    if (isAdmin) loadSettings();
  }, [isAdmin, isManager]);

  const handleRoleChange = async (uid, newRole) => {
    await updateUserProfile(uid, { role: newRole });
    setUsers(users.map(u => u.uid === uid ? { ...u, role: newRole } : u));
  };

  const handleDeleteUser = async (uid) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    await deleteUserDoc(uid);
    setUsers(users.filter(u => u.uid !== uid));
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    setResetPinError('');
    setResetPinSuccess('');
    
    if (!/^\d{6}$/.test(newPin)) {
      setResetPinError('PIN must be exactly 6 digits (e.g. 123456)');
      return;
    }
    
    try {
      const region = 'us-central1';
      const projectId = 'fieldsync-solutions';
      const res = await fetch(`https://resetworkerpin-lgrwjptv5a-uc.a.run.app`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: resetPinUid, newPin: newPin })
      });
      const data = await res.json();
      if (data.success) {
        setResetPinSuccess(`PIN reset for ${resetPinEmail}!`);
        setNewPin('');
        setResetPinUid(null);
      } else {
        setResetPinError(data.error || 'Failed to reset PIN');
      }
    } catch (err) {
      setResetPinError('Error: ' + err.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    if (isFieldWorkerRole && !/^\d{6}$/.test(newUser.password)) {
      setCreateError('PIN must be exactly 6 digits (e.g. 123456)');
      return;
    }

    const { uid, error } = await createUserAsAdmin(
      newUser.email,
      newUser.password,
      newUser.displayName,
      newUser.role
    );

    if (error) {
      setCreateError(error);
    } else {
      setCreateSuccess(`User created! UID: ${uid}`);
      setNewUser({ email: '', password: '', displayName: '', role: 'fieldWorker' });
      loadUsers();
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsError('');
    setSettingsSuccess('');
    
    if (!settings) return;
    
    const { error } = await updateCompanySettings(settings, currentUser?.uid);
    if (error) {
      setSettingsError(error);
    } else {
      setSettingsSuccess('Company settings saved successfully!');
      setTimeout(() => setSettingsSuccess(''), 3000);
    }
  };

  const updateFeatureToggle = (featureName, value) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [featureName]: value,
      }
    }));
  };

  if (!isAdmin && !isManager) {
    return (
      <div className="page">
        <p>Admins only. 🔒</p>
      </div>
    );
  }

  const roleBadge = (role) => {
    const colors = {
      admin: '#ef4444',
      fieldWorker: '#3b82f6',
      manager: '#f59e0b',
      pending: '#6b7280',
    };
    return (
      <span className="role-badge" style={{ backgroundColor: colors[role] || '#6b7280' }}>
        {role}
      </span>
    );
  };

  return (
    <div className="page admin">
      <h2>Admin Panel</h2>

      {/* ─── Company Settings ─── */}
      {isAdmin && (
        <section className="admin-section">
          <div className="admin-section-header">
            <h3>Company Settings</h3>
            <button className="btn-primary" onClick={() => setShowSettings(!showSettings)}>
              {showSettings ? 'Hide' : 'Configure'}
            </button>
          </div>

          {showSettings && settingsLoading && (
            <p>Loading settings...</p>
          )}

          {showSettings && !settingsLoading && settings && (
            <form className="admin-form" onSubmit={handleSaveSettings}>
              <div className="settings-grid">
                {/* Basic Info */}
                <div className="settings-group">
                  <h4>Company Info</h4>
                  <label>
                    Company Name
                    <input
                      type="text"
                      value={settings.companyName || ''}
                      onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                      placeholder="City Surface Repairs Ltd"
                    />
                  </label>
                  <label>
                    Logo URL
                    <input
                      type="text"
                      value={settings.companyLogo || ''}
                      onChange={(e) => setSettings({ ...settings, companyLogo: e.target.value })}
                      placeholder="https://.../logo.png"
                    />
                  </label>
                  <label>
                    Support Email
                    <input
                      type="email"
                      value={settings.supportEmail || ''}
                      onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                      placeholder="support@company.com"
                    />
                  </label>
                </div>

                {/* Zoho Integration */}
                <div className="settings-group">
                  <h4>Zoho Books Integration</h4>
                  <label>
                    Zoho Org ID
                    <input
                      type="text"
                      value={settings.zohoOrgId || ''}
                      onChange={(e) => setSettings({ ...settings, zohoOrgId: e.target.value })}
                      placeholder="20085027987"
                    />
                  </label>
                  <label>
                    Zoho Client ID
                    <input
                      type="text"
                      value={settings.zohoClientId || ''}
                      onChange={(e) => setSettings({ ...settings, zohoClientId: e.target.value })}
                      placeholder="OAuth Client ID"
                    />
                  </label>
                  <label>
                    Datacenter
                    <select
                      value={settings.zohoDatacenter || 'eu'}
                      onChange={(e) => setSettings({ ...settings, zohoDatacenter: e.target.value })}
                    >
                      <option value="eu">EU (eu)</option>
                      <option value="com">US (com)</option>
                      <option value="in">India (in)</option>
                      <option value="com.cn">China (com.cn)</option>
                      <option value="com.au">Australia (com.au)</option>
                    </select>
                  </label>
                </div>

                {/* Feature Toggles */}
                <div className="settings-group">
                  <h4>Feature Toggles</h4>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={settings.features?.clockInOutEnabled ?? true}
                      onChange={(e) => updateFeatureToggle('clockInOutEnabled', e.target.checked)}
                    />
                    <span>Clock In/Out Timesheet</span>
                  </label>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={settings.features?.locationTrackingEnabled ?? false}
                      onChange={(e) => updateFeatureToggle('locationTrackingEnabled', e.target.checked)}
                    />
                    <span>Location Tracking (GPS)</span>
                  </label>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={settings.features?.zohoIntegrationEnabled ?? false}
                      onChange={(e) => updateFeatureToggle('zohoIntegrationEnabled', e.target.checked)}
                    />
                    <span>Zoho Books Integration</span>
                  </label>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={settings.features?.multiDayJobsEnabled ?? true}
                      onChange={(e) => updateFeatureToggle('multiDayJobsEnabled', e.target.checked)}
                    />
                    <span>Multi-Day Jobs</span>
                  </label>
                  <label className="toggle-row">
                    <input
                      type="checkbox"
                      checked={settings.features?.clientSignatureEnabled ?? true}
                      onChange={(e) => updateFeatureToggle('clientSignatureEnabled', e.target.checked)}
                    />
                    <span>Client Signature Capture</span>
                  </label>
                </div>

                {/* Job Defaults */}
                <div className="settings-group">
                  <h4>Job Defaults</h4>
                  <label>
                    Default Start Time
                    <input
                      type="time"
                      value={settings.defaultJobStartTime || '08:00'}
                      onChange={(e) => setSettings({ ...settings, defaultJobStartTime: e.target.value })}
                    />
                  </label>
                  <label>
                    Default Work Hours
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={settings.defaultWorkHours || 8}
                      onChange={(e) => setSettings({ ...settings, defaultWorkHours: parseInt(e.target.value) })}
                    />
                  </label>
                </div>
              </div>

              {settingsError && <p className="form-error">{settingsError}</p>}
              {settingsSuccess && <p className="form-success">{settingsSuccess}</p>}
              <button type="submit" className="btn-primary">Save Settings</button>
            </form>
          )}
        </section>
      )}

      {/* ─── User Management ─── */}
      <section className="admin-section">
        <div className="admin-section-header">
          <h3>Users</h3>
          <button className="btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Cancel' : '+ Create User'}
          </button>
        </div>

        {showCreateForm && (
          <form className="admin-form" onSubmit={handleCreateUser}>
            <h4>Create New User</h4>
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Display Name"
              value={newUser.displayName}
              onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
            />
            <input
              type={isFieldWorkerRole ? 'number' : 'password'}
              placeholder={isFieldWorkerRole ? '6-Digit PIN (e.g. 123456)' : 'Temporary Password'}
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              required
              minLength={isFieldWorkerRole ? 6 : 6}
              maxLength={isFieldWorkerRole ? 6 : undefined}
              pattern={isFieldWorkerRole ? '[0-9]{6}' : undefined}
            />
            {isFieldWorkerRole && <p className="form-hint">Field workers log in with Email + this 6-digit PIN</p>}
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              {Object.keys(USER_ROLES).map((key) => (
                <option key={key} value={USER_ROLES[key]}>{USER_ROLES[key]}</option>
              ))}
            </select>
            {createError && <p className="form-error">{createError}</p>}
            {createSuccess && <p className="form-success">{createSuccess}</p>}
            <button type="submit" className="btn-primary">Create Account</button>
          </form>
        )}

        {usersLoading ? (
          <p>Loading users...</p>
        ) : users.length === 0 ? (
          <p className="empty">No users found.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.uid}>
                  <td>{u.displayName || '—'}</td>
                  <td>{u.email || '—'}</td>
                  <td>{roleBadge(u.role)}</td>
                  <td className="actions">
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                    >
                      {Object.values(USER_ROLES).map((r) => (
                        <option key={r} value={r}>Set to {r}</option>
                      ))}
                    </select>
                    {u.role === 'fieldWorker' && (
                      <button
                        className="btn-warning"
                        onClick={() => {
                          setResetPinUid(u.uid);
                          setResetPinEmail(u.email || u.displayName || 'User');
                          setNewPin('');
                          setResetPinError('');
                          setResetPinSuccess('');
                        }}
                      >
                        Reset PIN
                      </button>
                    )}
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteUser(u.uid)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ─── Admin Tools ─── */}
      <section className="admin-section">
        <h3>Admin Tools</h3>
        <div className="admin-tools-grid">
          <div className="admin-tool-card">
            <h4>Timesheet Recalculate</h4>
            <p>Recalculate all timesheet hours from clock-in/out data.</p>
            <div className="tool-actions">
              <button 
                className="btn btn-primary" 
                onClick={async () => {
                  try {
                    const region = 'us-central1';
                    const projectId = 'fieldsync-solutions';
                    const res = await fetch(`https://recalculatetimesheethours-lgrwjptv5a-uc.a.run.app`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' }
                    });
                    const data = await res.json();
                    alert(data.success ? `Recalculated ${data.updated} of ${data.total} timesheets` : `Error: ${data.error}`);
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                }}
              >
                Recalculate All Hours
              </button>
            </div>
          </div>

          <div className="admin-tool-card">
            <h4>Auth Event Log</h4>
            <p>Backfill all existing events with emails, or clear them.</p>
            <div className="tool-actions">
              <button 
                className="btn btn-primary" 
                onClick={async () => {
                  try {
                    const authEventsRef = collection(db, 'authEvents');
                    const snap = await getDocs(authEventsRef);
                    const batch = writeBatch(db);
                    let updated = 0;
                    
                    for (const docSnap of snap.docs) {
                      const event = docSnap.data();
                      if (event.userId && !event.userEmail) {
                        try {
                          const userSnap = await getDoc(doc(db, 'users', event.userId));
                          if (userSnap.exists() && userSnap.data().email) {
                            batch.update(docSnap.ref, { userEmail: userSnap.data().email });
                            updated++;
                          }
                        } catch (err) {
                          console.error('Error fetching user:', err);
                        }
                      }
                    }
                    
                    await batch.commit();
                    alert(`Updated ${updated} auth events with emails. Refresh the page!`);
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                }}
              >
                Backfill Emails (All Events)
              </button>
              <button 
                className="btn btn-warning" 
                onClick={async () => {
                  if (!window.confirm('Clear ALL auth events?')) return;
                  try {
                    const authEventsRef = collection(db, 'authEvents');
                    const snap = await getDocs(authEventsRef);
                    const batch = writeBatch(db);
                    snap.docs.forEach(doc => batch.delete(doc.ref));
                    await batch.commit();
                    alert(`Cleared ${snap.size} auth events.`);
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                }}
              >
                Clear All Events
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Auth Event Log ─── */}
      <section className="admin-section">
        <AuthEventLog />
      </section>
      {/* ─── PIN Reset Modal ─── */}
      {resetPinUid && (
        <div className="modal-overlay" onClick={() => setResetPinUid(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reset PIN for {resetPinEmail}</h3>
            <form onSubmit={handleResetPin}>
              <input
                type="number"
                placeholder="New 6-Digit PIN (e.g. 123456)"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                required
                minLength={6}
                maxLength={6}
                pattern="[0-9]{6}"
              />
              {resetPinError && <p className="form-error">{resetPinError}</p>}
              {resetPinSuccess && <p className="form-success">{resetPinSuccess}</p>}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Reset PIN</button>
                <button type="button" className="btn btn-secondary" onClick={() => setResetPinUid(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin;
